import { useState } from "react";
import { api, LOGO_URL, INSTAGRAM_URL } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, Instagram } from "lucide-react";

export default function Contact() {
  useSEO({
    title: "Επικοινωνία",
    description: "Επικοινωνήστε με τη Vanguard Lux Cars. Τηλέφωνο +30 693 322 3378, email luxcars@vanguardinfos.com.",
    path: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Παρακαλώ συμπληρώστε όνομα, email και μήνυμα.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Το μήνυμα στάλθηκε. Θα απαντήσουμε εντός 24 ωρών.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setSent(true);
    } catch (e) {
      toast.error("Η αποστολή απέτυχε. Δοκιμάστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <div className="label-caps mb-4">Επικοινωνία</div>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight uppercase mb-4">
            Επικοινωνήστε Μαζί Μας
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl font-light italic">
            Είτε ψάχνετε να αποκτήσετε το αυτοκίνητο των ονείρων σας είτε θέλετε ιδιωτική ενοικίαση — η ομάδα μας είναι στη διάθεσή σας.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Info panel */}
          <div className="space-y-10">
            <div>
              <img src={LOGO_URL} alt="Vanguard" className="h-16 w-16 object-contain mb-6" />
              <div className="font-display font-bold tracking-[0.2em] text-sm gold-gradient mb-1">
                VANGUARD
              </div>
              <div className="text-[0.65rem] tracking-[0.3em] text-gray-400 uppercase font-display">
                Lux Cars by Stekoza
              </div>
            </div>

            {[
              { icon: MapPin, label: "Έκθεση", value: "Η διεύθυνση δίνεται με το ραντεβού" },
              { icon: Phone, label: "Τηλέφωνο", value: "+30 693 322 3378", numeric: true },
              { icon: Mail, label: "Email", value: "luxcars@vanguardinfos.com" },
              { icon: Instagram, label: "Instagram", value: "@vanguardluxcars", href: INSTAGRAM_URL },
              { icon: Clock, label: "Ωράριο", value: "Δευτ – Σαβ: 09:00 – 20:00", numeric: true },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="h-10 w-10 border border-[var(--gold)]/30 flex items-center justify-center shrink-0">
                  <item.icon size={16} className="text-[var(--gold)]" />
                </div>
                <div>
                  <div className="label-caps mb-2">{item.label}</div>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-gray-300 font-light hover:text-[var(--gold)] transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div className={`text-base text-gray-300 whitespace-pre-line leading-relaxed ${item.numeric ? "font-display tracking-wider" : "font-light"}`}>
                      {item.value}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <form
              onSubmit={submit}
              className="bg-[#0e0e0e]/80 backdrop-blur-sm border border-[var(--gold)]/15 p-8 md:p-12"
            >
              {sent ? (
                <div className="text-center py-16">
                  <div className="h-16 w-16 mx-auto mb-6 border border-[var(--gold)] flex items-center justify-center">
                    <Send size={24} className="text-[var(--gold)]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold uppercase mb-3">Το Μήνυμα Παρελήφθη</h3>
                  <p className="text-base text-gray-400 font-light mb-8 max-w-md mx-auto">
                    Σας ευχαριστούμε που επικοινωνήσατε. Μέλος της ομάδας μας θα απαντήσει εντός 24 ωρών.
                  </p>
                  <button
                    type="button"
                    data-testid="send-another-btn"
                    onClick={() => setSent(false)}
                    className="btn-secondary"
                  >
                    Στείλε Άλλο
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
                    <div>
                      <label className="label-caps block mb-2">Ονοματεπώνυμο *</label>
                      <input
                        data-testid="contact-name"
                        type="text"
                        required
                        className="input-line"
                        placeholder="Όνομα Επώνυμο"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-caps block mb-2">Email *</label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        required
                        className="input-line"
                        placeholder="email@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-caps block mb-2">Τηλέφωνο</label>
                      <input
                        data-testid="contact-phone"
                        type="tel"
                        className="input-line"
                        placeholder="+30 69X XXX XXXX"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-caps block mb-2">Θέμα</label>
                      <select
                        data-testid="contact-subject"
                        className="select-dark"
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      >
                        <option value="">Επιλέξτε θέμα...</option>
                        <option value="purchase">Αίτημα Αγοράς</option>
                        <option value="rental">Αίτημα Ενοικίασης</option>
                        <option value="consignment">Παράδοση Αυτοκινήτου προς Πώληση</option>
                        <option value="general">Γενική Ερώτηση</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-caps block mb-2">Μήνυμα *</label>
                      <textarea
                        data-testid="contact-message"
                        rows={6}
                        required
                        className="input-line resize-none"
                        placeholder="Πες μας πώς μπορούμε να βοηθήσουμε..."
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                    <p className="text-xs text-gray-500 tracking-widest uppercase max-w-md font-light leading-relaxed font-display">
                      Τα στοιχεία σας είναι εμπιστευτικά και θα χρησιμοποιηθούν μόνο για να σας απαντήσουμε.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="btn-primary flex items-center gap-3"
                    >
                      {submitting ? "Αποστολή..." : "Αποστολή"}
                      <Send size={14} />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
